import type { World } from './world';
import type { HealthComponent } from './entity';

/**
 * Player (and, generally, mobile) **health**: a real, replicated per-mobile
 * attribute (Duel slice A, PRD #252, ADR-0128). It is the substrate all future
 * combat drains - duels are its only consumer for now - carried in the entity
 * snapshot so every client reads a wounded mobile's health authoritatively, and
 * a **live-world attribute only**: initialised to the maximum on spawn/login and
 * never written to the account store, so a relog starts at full (the additive-
 * optional gold precedent governs any future promotion to a persisted field).
 *
 * This module is the co-located home of the tunables plus the pure, clock-free
 * regen and restore logic, mirroring how `ratSwarm.ts` keeps its constants and
 * resolver together. Everything here reads no wall clock and no unseeded
 * randomness - time enters only as the `stepMs` fed to {@link healthSystem} -
 * so it stays on the deterministic side of the sim contract (`architecture.test.ts`).
 */

/** Full health a mobile spawns (and is restored) to. Tunable. */
export const MAX_HEALTH = 100;

/**
 * Wall-clock a mobile at zero takes to passively regenerate to {@link MAX_HEALTH}
 * while not in a duel. Tunable; the per-ms rate is derived from it and the
 * mobile's own `max`, so a mobile with a smaller pool still refills in this time.
 */
export const REGEN_TO_FULL_MS = 45_000;

/**
 * Build a fresh, full-health component. The starting pool defaults to
 * {@link MAX_HEALTH}; `max` is captured so regen and restore target the mobile's
 * own maximum rather than the global constant.
 */
export function createHealth(max: number = MAX_HEALTH): HealthComponent {
  return { current: max, max };
}

/**
 * Restore a mobile to full immediately - the seam the duel-end path calls so a
 * defeated player is never left wounded with no way to heal. Clears any regen
 * pause in the same stroke, since a restored mobile is out of combat.
 */
export function restoreToFull(health: HealthComponent): void {
  health.current = health.max;
  health.regenPaused = false;
}

/**
 * Passively regenerate one mobile's health toward its maximum by `dtMs` of
 * elapsed sim time. A no-op while {@link HealthComponent.regenPaused} (the mobile
 * is in a duel) or already at full; otherwise it adds `max / REGEN_TO_FULL_MS`
 * health per millisecond, clamped to `max`. Pure: it reads only its arguments,
 * so advancing ticks is the only way health moves.
 */
export function regenHealth(health: HealthComponent, dtMs: number): void {
  if (health.regenPaused) return;
  if (health.current >= health.max) return;
  const perMs = health.max / REGEN_TO_FULL_MS;
  health.current = Math.min(health.max, health.current + perMs * dtMs);
}

/**
 * The health system: advance every health-bearing entity's passive regen by one
 * fixed step. A pure function over the {@link World} run from {@link World.tick}
 * alongside {@link movementSystem}; it touches only the `health` component, so a
 * mobile with no vitals (a door, a dice prop, a teleporter, a rat) is skipped.
 * Reads no wall clock or unseeded randomness, keeping the sim deterministic.
 */
export function healthSystem(world: World, stepMs: number): void {
  for (const entity of world.all()) {
    if (entity.health) regenHealth(entity.health, stepMs);
  }
}
